// `TypstWriter` and `TypstDocument` (SuiteCompile/TextWriters.swift): Typst markup for every PDF preset, with a map
// from source byte ranges back to the manuscript, so the layout (which reports each line's source position) can be
// traced to paragraphs and fragments. The output must be the Swift's byte for byte (fixtures/mac-typst): Doubles are
// written as Swift's `description` writes them ("11.0", "15.399999999999999"), text escaped Character by Character.
import { blockPlainText, listStyleOf, marker, startsList, verseLines, wordCountOfPlainText, VERSE_INDENT_EM, type Block, type Inline, type ListStyle } from '@writing-suite/core';
import {
  authorNames, contactName, contentsEntries, documentTitle, header as fillHeader, heading, isDocument, pageInches, resolvedMargins, titlePageContent,
} from '../manuscript.ts';
import { swiftDoubleDescription } from '../presets.ts';
import type { BookMatter, CompiledManuscript, CompiledSection, PrintSettings } from '../types.ts';

export type RegionKind =
  | { type: 'paragraph' }
  | { type: 'heading' }
  /** A scene break; `index` counts every break in the manuscript, `blank` when it has no ornament. */
  | { type: 'sceneBreak'; index: number; blank: boolean };

/** `TypstDocument.Region`: a stretch of the source, in UTF-8 bytes. */
export interface Region {
  start: number;
  end: number;
  kind: RegionKind;
  /** Index into the manuscript's sections. */
  section: number;
  /** Index into the section's blocks, for paragraphs and in-text breaks. */
  block: number | null;
}

/** `TypstDocument`: the source and its regions (in source order, not overlapping). */
export interface TypstDocument {
  source: string;
  regions: Region[];
}

/** `TypstDocument.BlockKey`, as "section:block" (stable between typesetting passes). */
export type BlockKey = string;
export const blockKey = (section: number, block: number): BlockKey => `${section}:${block}`;
export const parseBlockKey = (key: BlockKey): { section: number; block: number } => {
  const [section, block] = key.split(':').map(Number);
  return { section, block };
};

/** Swift's `\(double)`. */
const d = swiftDoubleDescription;

/** UTF-8 length of a string (Swift's `utf8.count`); a lone surrogate counts as U+FFFD would. */
export function utf8Length(s: string): number {
  let n = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 0x80) n += 1;
    else if (c < 0x800) n += 2;
    else if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length && (s.charCodeAt(i + 1) & 0xfc00) === 0xdc00) { n += 4; i++; }
    else n += 3;
  }
  return n;
}

/** Source text with its UTF-8 length kept as it grows. */
class Out {
  private parts: string[] = [];
  bytes = 0;
  add(s: string): void {
    this.parts.push(s);
    this.bytes += utf8Length(s);
  }
  toString(): string {
    return this.parts.join('');
  }
}

// MARK: Escaping

const SPECIAL = new Set('\\#*_`$<>@[]=-+/~');
/** Code points that join the one before into one Character (grapheme cluster), as far as escaping can tell. */
const EXTENDER = /^[\p{M}\u200D\uFE0E\uFE0F\p{Emoji_Modifier}]$/u;

/** `TypstWriter.escape`: text safe for Typst markup. A full stop after a digit is escaped too (`1999. She…` would
 *  start a numbered list at a line's start); control characters are dropped, tabs become spaces. Per Character: a
 *  special character with a combining mark after it is another Character, and isn't escaped. */
export function escape(s: string, smartQuotes = false): string {
  let out = '';
  let afterDigit = false;
  const cps = Array.from(s);
  for (let i = 0; i < cps.length; i++) {
    const c = cps[i];
    const next = cps[i + 1];
    if (c === '\r' && next === '\n') { out += '\r\n'; i++; afterDigit = false; continue; }
    const extended = next !== undefined && EXTENDER.test(next);
    if (!extended) {
      const v = c.codePointAt(0)!;
      if (v < 0x20 || v === 0x7f) {
        if (c === '\t') out += ' ';
        afterDigit = false;
        continue;
      }
      if (SPECIAL.has(c) || (!smartQuotes && (c === '"' || c === "'")) || (afterDigit && c === '.')) out += '\\';
    }
    out += c;
    afterDigit = !extended && c >= '0' && c <= '9';
  }
  return out;
}

/** `TypstWriter.escapeString`: for a Typst string literal. */
export const escapeString = (s: string) => s.replaceAll('\\', '\\\\').replaceAll('"', '\\"');

/** `trimmingCharacters(in: .whitespaces)`: spaces (Zs) and tabs, not newlines. */
export const trimWhitespaces = (s: string) => s.replace(/^[\p{Zs}\t]+|[\p{Zs}\t]+$/gu, '');

// MARK: Blocks

/** `TypstWriter.inlines`. */
export function inlines(list: Inline[], smartQuotes = false): string {
  return list.map((inline) => {
    switch (inline.type) {
      case 'text': {
        let s = escape(inline.text, smartQuotes);
        if (inline.style.smallCaps) s = `#smallcaps[${s}]`;
        if (inline.style.underline) s = `#underline[${s}]`;
        if (inline.style.bold) s = `#strong[${s}]`;
        if (inline.style.italic) s = `#emph[${s}]`;
        return s;
      }
      case 'lineBreak': return ' \\\n';
      case 'footnoteReference': return `#super[${escape(inline.id)}]`;
      case 'linkStart': return `#link("${escapeString(inline.address)}")[`;
      case 'linkEnd': return ']';
    }
  }).join('');
}

/** `String(format: "%.4f", x)`. */
const fixed4 = (x: number) => x.toFixed(4);

/** `TypstWriter.content`: a block's inlines, tracked looser when the copyfit loop asks. */
export function content(block: Block, smartQuotes: boolean, tracking: number | undefined): string {
  const c = inlines(block.inlines, smartQuotes);
  return tracking === undefined ? c : `#text(tracking: ${fixed4(tracking)}em)[${c}]`;
}

/** `TypstWriter.listOpening`: a list call's opening; its items follow as `[…],` and a `)` closes it. */
export function listOpening(style: ListStyle, start: number): string {
  switch (style) {
    case 'bullet': return '#list(marker: [•],';
    case 'circle': return '#list(marker: [◦],';
    case 'dash': return '#list(marker: [–],';
    default: return `#enum(numbering: "${marker(style, 1)}", start: ${Math.max(1, start)},`;
  }
}

/** `TypstWriter.markup`: one block. A list item on its own is a list of one. */
export function markup(block: Block, noIndent: boolean, smartQuotes = false, tracking?: number): string {
  const c = content(block, smartQuotes, tracking);
  switch (block.kind.type) {
    case 'sceneBreak': return '#scenebreak[]';
    case 'paragraph': {
      const alignment = block.alignment;
      if (alignment === null) return noIndent ? `#par(first-line-indent: 0pt)[${c}]` : c;
      // Centred and right-aligned lines are never justified or indented.
      if (alignment !== 'justify') return `#align(${alignment})[#par(first-line-indent: 0pt, justify: false)[${c}]]`;
      return noIndent ? `#par(first-line-indent: 0pt, justify: true)[${c}]` : `#par(justify: true)[${c}]`;
    }
    case 'listItem': return listOpening(block.kind.style, block.kind.number) + ` [${c}])`;
    case 'blockQuote': return `#quote(block: true)[${c}]`;
    case 'epigraph': return `#pad(left: 2em)[#emph[${c}]]`;
    case 'verse': return verse(block, smartQuotes, tracking);
    case 'footnote': return `#pad(left: 2em)[#text(size: 0.85em)[${c}]]`;
  }
}

/** `TypstWriter.verse`: a stanza, each line its own paragraph (so a step is the line's own indent and a wrapped line
 *  hangs under its first word), ragged, unhyphenated, unindented, spaced by the book's leading, kept whole on a page when
 *  it fits; the space round it is one blank line (weak, so two stanzas share it), and a line with no text is a line high
 *  (`#verseblank`). Centred or right-aligned verse keeps its lines together with hard breaks and no steps. */
export function verse(block: Block, smartQuotes: boolean, tracking: number | undefined): string {
  const lines = verseLines(block);
  const text = (line: { inlines: Inline[] }) => {
    const s = inlines(line.inlines, smartQuotes);
    return tracking === undefined ? s : `#text(tracking: ${fixed4(tracking)}em)[${s}]`;
  };
  if (block.alignment !== null && block.alignment !== 'justify') {
    const body = lines.map((l) => (text(l) === '' ? '#verseblank' : text(l))).join(' \\\n');
    return `#stanza[#align(${block.alignment})[#par(first-line-indent: 0pt, justify: false)[${body}]]]`;
  }
  const pars = lines.map((line) => {
    const indent = line.indent * VERSE_INDENT_EM;
    const lead = line.indent > 0 ? `#h(${em(indent)})` : '';
    const body = text(line);
    return `#par(hanging-indent: ${em(indent + 1.5)})[${lead}${body === '' ? '#verseblank' : body}]`;
  });
  return '#stanza[' + pars.join('\n\n') + '\n]';
}

/** `TypstWriter.em`: a length in ems, written without a trailing ".0". */
export const em = (value: number) => (Number.isInteger(value) ? `${value}em` : `${d(value)}em`);

/** `TypstWriter.linesOf`: how many lines a section sets, roughly — a verse line each, a paragraph as its lines at forty
 *  words. */
export function linesOf(section: CompiledSection): number {
  return section.blocks.reduce((count, block) => {
    switch (block.kind.type) {
      case 'verse': return count + verseLines(block).length + 1;
      case 'sceneBreak': return count + 1;
      default: return count + Math.max(1, Math.floor(wordCountOfPlainText(blockPlainText(block)) / 10));
    }
  }, 0);
}

// MARK: Book pages

/** `TypstWriter.bookPageKinds`: book matter with a layout of its own rather than a chapter-style heading. */
export const BOOK_PAGE_KINDS: ReadonlySet<BookMatter> = new Set<BookMatter>(['half-title', 'title-page', 'copyright', 'dedication', 'epigraph', 'contents']);

/** `TypstWriter.bookPage(_:section:…)`: a front-matter page with its own layout, or null for one set like a chapter.
 *  `side`: the page it starts on in a printed book, odd (right) or even (left). */
export function matterPage(matter: BookMatter, section: CompiledSection, blocks: string, m: CompiledManuscript,
  h: number, top: number): { side: string | null; markup: string } | null {
  const r = m.preset.render;
  const drop = `#v(${d(Math.max(0, h / 3 - top))}in)\n`;
  switch (matter) {
    case 'half-title':
      return { side: 'odd', markup: drop + `#align(center)[#text(size: 1.5em)[${blocks}]]\n` };
    case 'title-page': {
      const parts = section.blocks.filter((b) => b.kind.type !== 'sceneBreak').map((b) => markup(b, true, r.print !== undefined));
      const titleBlock = parts.length ? `#text(size: 2em)[${parts[0]}]` : '';
      const rest = parts.slice(1).map((p) => `#text(size: 1.2em)[${p}]`).join('\n\n');
      return { side: 'odd', markup: drop + `#align(center)[${titleBlock}\n\n#v(2em)\n${rest}]\n` };
    }
    case 'copyright':
      return { side: 'even', markup: `#v(1fr)\n#block[\n#set text(size: 0.8em)\n#set par(first-line-indent: 0pt, justify: false, spacing: 1em)\n${blocks}\n]\n` };
    case 'dedication':
      return { side: 'odd', markup: drop + `#align(center)[#emph[${blocks}]]\n` };
    case 'epigraph':
      return { side: 'odd', markup: drop + `#align(center)[#block(width: 80%)[#set align(left)\n#set par(first-line-indent: 0pt)\n#emph[${blocks}]]]\n` };
    case 'contents': {
      // Parts, chapters and headed back matter, each linked, with its page number.
      let entries = '';
      for (const { section: s, text } of contentsEntries(m)) {
        if (s.fragmentID === null) continue;
        const id = s.fragmentID;
        const space = s.role === 'part' ? '#v(0.8em)\n' : '';
        const shown = s.role === 'part' ? `#smallcaps[${escape(text)}]` : escape(text);
        entries += `${space}#par(first-line-indent: 0pt, justify: false)[#link(<frag-${id}>)[${shown}]#h(0.4em)#box(width: 1fr, repeat(gap: 0.3em)[.])#h(0.4em)#context counter(page).at(<frag-${id}>).first()]\n`;
      }
      const head = `#align(center)[#text(size: ${d(r.font.size * (r.print?.headingScale ?? 1))}pt)[${escape(section.title)}]]\n#v(2em)\n`;
      return { side: 'odd', markup: `#v(${d(r.chapterDrop)}in)\n` + head + entries };
    }
    default:
      return null;
  }
}

/** `TypstWriter.bookPage(_:book:width:height:)`: facing pages with mirrored margins; running heads on text pages only,
 *  folios on every page but the front matter and the blank pages left before recto openers. */
export function bookSetup(m: CompiledManuscript, book: PrintSettings, w: number, h: number): string {
  const r = m.preset.render;
  const margins = resolvedMargins(r.page);
  const verso = escape(fillHeader(book.versoHead, m));
  const recto = escape(fillHeader(book.rectoHead, m));
  const bleed = book.bleed > 0 ? `, bleed: (top: ${d(book.bleed)}in, bottom: ${d(book.bleed)}in, outside: ${d(book.bleed)}in, inside: 0pt)` : '';
  return `#let no-furniture() = {
  let p = here().page()
  let heads = query(heading)
  // Nothing before the text begins: title pages, copyright, contents.
  let starts = query(<body-start>)
  if starts.len() == 0 or starts.first().location().page() > p { return true }
  let ends = query(<chapter-end>).filter(m => m.location().page() < p)
  if ends.len() > 0 {
    let last = ends.last().location().page()
    let next = heads.filter(h => h.location().page() > last)
    if next.len() > 0 and next.first().location().page() > p { return true }
  }
  false
}
#let opener() = query(heading).any(h => h.location().page() == here().page())
#set page(width: ${d(w)}in, height: ${d(h)}in, binding: left${bleed},
  margin: (inside: ${d(margins.inside)}in, outside: ${d(margins.outside)}in, top: ${d(margins.top)}in, bottom: ${d(margins.bottom)}in),
  header: context { if not no-furniture() and not opener() {
    align(center, text(size: 0.8em, tracking: 0.08em, upper(if calc.even(here().page()) [${verso}] else [${recto}])))
  } },
  footer: context { if not no-furniture() { align(center, text(size: 0.9em, counter(page).display())) } })
#set text(costs: (widow: ${book.preventWidows ? 100 : 0}%, orphan: ${book.preventWidows ? 100 : 0}%, runt: 250%))
// Names aren't hyphenated. (Letters only: matching an apostrophe would bypass smart quotes.)
#show regex("\\\\b\\\\p{Lu}\\\\p{L}+"): set text(hyphenate: false)
`;
}

// MARK: The document

export interface DocumentOptions {
  /** Blank scene breaks (by index) to set with the print ornament, because they fell at a page edge. */
  ornamentBreaks?: ReadonlySet<number>;
  /** Extra tracking (em) for paragraphs, so a short page gains a line (spread balancing). */
  loosen?: ReadonlyMap<BlockKey, number>;
  /** `{year}` in a document's title lines (the Swift takes today's). */
  year?: number;
}

/** The font list the source's `#set text(font: …)` names, for loading fonts by preset. */
export function typstFontFamilies(m: CompiledManuscript): string[] {
  return [m.preset.render.font.family, 'Times New Roman', 'Libertinus Serif'];
}

/** `TypstWriter.source`. */
export function typstSource(m: CompiledManuscript, o: DocumentOptions = {}): string {
  return typstDocument(m, o).source;
}

/** `TypstWriter.document`: the source for the manuscript and its regions. */
export function typstDocument(m: CompiledManuscript, o: DocumentOptions = {}): TypstDocument {
  const ornamentBreaks = o.ornamentBreaks ?? new Set<number>();
  const loosen = o.loosen ?? new Map<BlockKey, number>();
  const r = m.preset.render;
  const book = r.print;
  const { width: w, height: h } = pageInches(r.page.size);
  const out = new Out();
  const leading = Math.max(0.2, (r.lineSpacing - 1) * 1.0 + 0.65);
  out.add(`#set document(title: "${escapeString(m.title)}", author: (${m.author ? authorNames(m.author).map((n) => `"${escapeString(n)}",`).join(' ') : ''}))
#set text(font: ("${escapeString(r.font.family)}", "Times New Roman", "Libertinus Serif"), size: ${d(r.font.size)}pt, lang: "${[...m.language].slice(0, 2).join('')}", hyphenate: ${r.justify})
#set par(leading: ${d(leading)}em, spacing: ${r.paragraphSpacing > 0 ? `${d(r.paragraphSpacing)}pt` : `${d(leading)}em`}, first-line-indent: (amount: ${d(r.firstLineIndent)}in, all: false), justify: ${r.justify})
#show heading: set align(center)
#show heading: set text(size: ${d(r.font.size * (book?.headingScale ?? 1))}pt, weight: "regular")
#let scenebreak(body) = align(center, block(above: 1em, below: 1em, body))
#let stanza(body) = context block(breakable: false, above: ${em(2 * leading)} + measure[X].height, below: ${em(2 * leading)} + measure[X].height)[#set par(spacing: ${em(leading)}, first-line-indent: 0pt, justify: false)
#set text(hyphenate: false)
#body]
#let verseblank = context box(height: measure[X].height)
`);
  if (book) {
    // PDF/X for a printer: black as 100% K (DeviceCMYK). Typst's default black is a grey that goes out through an ICC
    // profile, which PDF/X-1a forbids and IngramSpark warns turns text grey.
    if (book.pdfx !== undefined) out.add('#set text(fill: cmyk(0%, 0%, 0%, 100%))\n#set line(stroke: cmyk(0%, 0%, 0%, 100%))\n');
    out.add(bookSetup(m, book, w, h));
  } else {
    const head = fillHeader(r.header, m);
    const headerContent = head === '' ? 'none' : (() => {
      const parts = head.split('{page}').map((p) => `[${escape(p)}]`);
      return `context { if counter(page).get().first() > ${r.titlePage.include ? 1 : 0} { align(right, ` + parts.join(' + counter(page).display() + ') + ') } }';
    })();
    const footer = m.readerMark !== null ? `align(center, text(size: 7.5pt, fill: luma(110))[${escape(m.readerMark)}])` : 'auto';
    out.add(`#set page(width: ${d(w)}in, height: ${d(h)}in, margin: ${d(r.page.margins)}in, header: ${headerContent}, footer: ${footer})\n`);
  }
  if (isDocument(m)) {
    if (r.letterhead === true && m.author) {
      out.add(`#par(first-line-indent: 0pt, justify: false)[${[contactName(m.author), ...m.author.contact].map((s) => escape(s)).join(' \\\n')}]\n#v(1em)\n`);
    }
    const lines = documentTitle(m, o.year);
    if (lines.length) {
      out.add(`#align(center)[#strong[${escape(lines[0])}]${lines.slice(1).map((l) => ` \\\n${escape(l)}`).join('')}]\n#v(1em)\n`);
    }
  }
  // A title page written in the Front Matter folder replaces the preset's.
  const tp = titlePageContent(m);
  const titleShown = tp !== null;
  if (tp) {
    const words = tp.wordCount !== null ? escape(tp.wordCount) : '';
    if (tp.contact.length) out.add(`#grid(columns: (1fr, auto), [${tp.contact.map((s) => escape(s)).join(' \\\n')}], [${words}])\n`);
    else if (words !== '') out.add(`#align(right)[${words}]\n`);
    out.add(`#v(${d(h / 3 - resolvedMargins(r.page).top)}in)\n#align(center)[${escape(tp.title)}${tp.byline !== null ? ` \\\n\\\n${escape(tp.byline)}` : ''}]\n`);
  }
  const regions: Region[] = [];
  const region = (kind: RegionKind, section: number, block: number | null, text: string) => {
    const start = out.bytes;
    out.add(text);
    regions.push({ start, end: out.bytes, kind, section, block });
  };
  let breaks = 0;
  const sceneBreak = (section: number, block: number | null) => {
    let ornament = trimWhitespaces(r.sceneBreak);
    if (ornament === '' && ornamentBreaks.has(breaks) && book) ornament = book.pageEdgeOrnament;
    region({ type: 'sceneBreak', index: breaks, blank: ornament === '' }, section, block, `#scenebreak[${escape(ornament)}]`);
    out.add('\n\n');
    breaks += 1;
  };
  let first = true;
  let numbered = false;
  let keptWhole = false;
  const top = resolvedMargins(r.page).top;
  const newPage = (side: string | null) => {
    if (first && !titleShown) return;
    if (book) out.add('#metadata(none) <chapter-end>\n');
    out.add(`#pagebreak(weak: true${book && side !== null ? `, to: "${side}"` : ''})\n`);
  };
  const blocksOf = (section: CompiledSection) =>
    section.blocks.filter((b) => b.kind.type !== 'sceneBreak').map((b) => markup(b, true, book !== undefined)).join('\n\n');

  for (const [index, section] of m.sections.entries()) {
    const label = section.fragmentID !== null ? ` <frag-${section.fragmentID}>` : '';
    // Front-matter pages laid out as a book lays them out, without a heading.
    const page = section.matter !== null ? matterPage(section.matter, section, blocksOf(section), m, h, top) : null;
    if (page) {
      newPage(page.side);
      out.add(page.markup + (label === '' ? '' : `#metadata(none)${label}\n`) + '\n');
      first = false;
      continue;
    }
    switch (section.role) {
      case 'part': case 'chapter': case 'front-matter': case 'back-matter': {
        const lines = section.role === 'part' ? heading(r.partHeading, section)
          : section.role === 'chapter' ? heading(r.chapterHeading, section) : [section.title];
        if (r.chapterStartsNewPage && !(first && !titleShown)) {
          if (book) out.add('#metadata(none) <chapter-end>\n');
          out.add(`#pagebreak(weak: true${book?.chapterOpensRecto === true ? ', to: "odd"' : ''})\n`);
        }
        if (book && !numbered && section.role !== 'front-matter') {
          out.add('#counter(page).update(1)\n#metadata(none) <body-start>\n');
          numbered = true;
        }
        out.add(`#v(${d(r.chapterDrop)}in)\n`);
        if (lines.length === 0) {
          // No heading (a run of short poems): the drop alone parts it from the one before.
          if (label !== '') out.add(`#metadata(none)${label}\n`);
          out.add('\n');
        } else {
          region({ type: 'heading' }, index, null, `#heading(level: ${section.role === 'part' ? 1 : 2})[${lines.map((l) => escape(l)).join(' \\\n')}]`);
          out.add(`${label}\n#v(${d(Math.max(1.5, r.lineSpacing * 1.2))}em)\n\n`);
        }
        if (section.role === 'chapter' && r.keepChaptersWhole === true && linesOf(section) <= 30) {
          // A short poem kept whole on a page (moved on when it doesn't fit); its own stanzas are blocks too.
          out.add('#block(breakable: false)[\n');
          keptWhole = true;
        }
        break;
      }
      default:
        if (section.followsScene) sceneBreak(index, null);
        if (label !== '') out.add(`#metadata(none)${label}\n`);
    }
    let afterHeading = section.role !== 'scene';
    let previous = null as Block | null;
    for (const [b, block] of section.blocks.entries()) {
      // A list is one `#enum`/`#list` call, each item a content argument of its own (and a region).
      if (previous && listStyleOf(previous) !== null && (listStyleOf(block) === null || startsList(block, previous))) out.add(')\n\n');
      if (startsList(block, previous) && block.kind.type === 'listItem') out.add(listOpening(block.kind.style, block.kind.number) + '\n');
      previous = block;
      const tracking = loosen.get(blockKey(index, b));
      if (block.kind.type === 'sceneBreak') {
        sceneBreak(index, b);
      } else if (listStyleOf(block) !== null) {
        region({ type: 'paragraph' }, index, b, '[' + content(block, book !== undefined, tracking) + '],');
        out.add('\n');
      } else {
        region({ type: 'paragraph' }, index, b, markup(block, afterHeading, book !== undefined, tracking));
        out.add('\n\n');
      }
      afterHeading = block.kind.type === 'sceneBreak';
    }
    if (previous && listStyleOf(previous) !== null) out.add(')\n\n');
    if (keptWhole) { out.add(']\n\n'); keptWhole = false; }
    first = false;
  }
  if (r.endMarker !== '') out.add(`#align(center)[${escape(r.endMarker)}]\n`);
  return { source: out.toString(), regions };
}

/** A region's paragraph as plain text (for quoting a finding). */
export function regionText(m: CompiledManuscript, region: Region): string | null {
  if (region.block === null) return null;
  const blocks = m.sections[region.section].blocks;
  return region.block < blocks.length ? blockPlainText(blocks[region.block]) : null;
}
